import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Camera, Loader2 } from 'lucide-react'
import ErrorBanner from '@/components/ErrorBanner'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { turbineService, type Photo } from '@/services/turbineService'
import { getPhotoBlade } from '@/utils/photoHelpers'
import { type CategoryKey, type CategoryTheme } from '../constants'

// Photo Sidebar Component
const PhotoSidebar: React.FC<{
  turbineId: number,
  selectedPhoto: Photo | null,
  onPhotoSelect: (photo: Photo) => void,
  isOpen: boolean,
  refreshSignal?: number,
  onPhotosLoaded?: (photos: Photo[]) => void,
  onVisiblePhotosChange?: ((photos: Photo[]) => void) | undefined,
  category: CategoryKey,
  theme: CategoryTheme
}> = ({ turbineId, selectedPhoto, onPhotoSelect, isOpen, refreshSignal, onPhotosLoaded, onVisiblePhotosChange, category, theme }) => {
  const [photos, setPhotos] = useState<Photo[]>([])
  const [loading, setLoading] = useState(true)
  // Yukleme hatasi ayri tutulur: liste HIC bosaltilmaz, elde ne varsa gosterilmeye
  // devam eder ve ustunde neden + "Tekrar dene" cikar.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedBlade, setSelectedBlade] = useState<string>('all')
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false)
  const [isBladeDropdownOpen, setIsBladeDropdownOpen] = useState(false)
  const yearDropdownRef = useRef<HTMLDivElement>(null)
  const bladeDropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useLanguage()
  const { notifyWarning } = useNotifications()

  // Keep latest callback in a ref so loadPhotos identity stays stable —
  // otherwise every photo selection reset the polling interval and triggered
  // an immediate full force-refresh (re-downloading all thumbnails).
  const onPhotosLoadedRef = useRef(onPhotosLoaded)
  useEffect(() => {
    onPhotosLoadedRef.current = onPhotosLoaded
  }, [onPhotosLoaded])

  // Track current list to compare against fresh responses
  const photosRef = useRef<Photo[]>([])

  // Delta sync uyarisi dakikada bir tekrar edilmesin diye tek seferlik bayrak.
  const syncFailureNotifiedRef = useRef(false)

  // Metinler ref'ten okunur: `t` dogrudan bagimlilik olsaydi dil secimi
  // loadPhotos kimligini degistirir, yukaridaki poll araligini sifirlar ve
  // tum kucuk resimleri yeniden indirtirdi.
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  // Two photo lists are "equal" if the same files are present —
  // presigned URLs differ on every refresh, so they must be ignored here.
  const photoListsEqual = (a: Photo[], b: Photo[]) => {
    if (a.length !== b.length) return false
    return a.every((photo, i) => {
      const other = b[i]
      return !!other && photo.key === other.key && photo.lastModified === other.lastModified
    })
  }

  const loadPhotos = useCallback(async (showLoader: boolean = true, forceRefresh: boolean = false) => {
    if (showLoader) setLoading(true)
    try {
      const response = await turbineService.getAllPhotos(turbineId, undefined, undefined, forceRefresh)
      if (response.success && response.data) {
        setLoadError(null)
        const categoryPhotos = response.data.filter((photo: Photo) => photo.category === category)
        // Skip state update when nothing changed: new presigned URLs would
        // otherwise change every <img> src and re-download all thumbnails.
        if (!forceRefresh && photoListsEqual(photosRef.current, categoryPhotos)) {
          return
        }
        photosRef.current = categoryPhotos
        setPhotos(categoryPhotos)
        onPhotosLoadedRef.current?.(categoryPhotos)
      } else if (!response.success) {
        // success:false bir hatadir; "fotograf bulunamadi" diye gostermek yanlis olur.
        // (success:true + bos data hali eskisi gibi sessiz gecilir: hata degil.)
        setLoadError(responseErrorMessage(response, tRef.current, tRef.current.errors.loadFailed))
      }
    } catch (error) {
      logError('PhotoSidebar.loadPhotos', error)
      // Mevcut liste KORUNUR; kullanici hem eldeki fotograflari gorur hem de
      // listenin guncel olmayabilecegini bilir.
      setLoadError(toUserMessage(error, tRef.current, tRef.current.errors.loadFailed))
    } finally {
      if (showLoader) setLoading(false)
    }
  }, [category, turbineId])

  useEffect(() => {
    loadPhotos(true, false)

    // Delta sync: once a minute ask the backend for its lightweight sync-state
    // (COUNT + MAX(uploaded_at)). The full list is only re-fetched when it
    // actually changed — no DB reads, no presigned URL churn otherwise.
    const interval = setInterval(async () => {
      try {
        const changed = await turbineService.checkPhotoUpdates(turbineId)
        if (changed) {
          loadPhotos(false, false)
        }
        syncFailureNotifiedRef.current = false
      } catch (error) {
        logError('PhotoSidebar.deltaSync', error)
        // Senkron sessizce olurse liste bayatlar ve kullanici bunu fark etmez.
        // Uyari dakikada bir tekrarlanmasin diye ilk hatada bir kez verilir;
        // bir sonraki BASARILI senkronda bayrak sifirlanir.
        if (!syncFailureNotifiedRef.current) {
          syncFailureNotifiedRef.current = true
          notifyWarning(
            'Fotoğraf listesi güncellenemiyor', // TODO(i18n)
            toUserMessage(error, tRef.current, 'Liste güncel olmayabilir. Yenilemek için tekrar deneyin.') // TODO(i18n)
          )
        }
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [loadPhotos, turbineId, notifyWarning])

  useEffect(() => {
    if (refreshSignal) {
      loadPhotos(false, true)
    }
  }, [refreshSignal, loadPhotos])

  // Filter photos based on blade and year
  const filteredPhotos = photos.filter(photo => {
    // Extract blade info from photo path or metadata
    const photoYear = photo.year || new Date(photo.upload_date || photo.createdAt || new Date().toISOString()).getFullYear()
    const yearMatch = photoYear === selectedYear

    if (selectedBlade === 'all') {
      return yearMatch
    }

    const blade = getPhotoBlade(photo)
    const bladeMatch = blade ? blade.toUpperCase() === selectedBlade.toUpperCase() : false

    return yearMatch && bladeMatch
  })

  // Report the currently visible (filtered) photos to the parent so the
  // fullscreen viewer can navigate through exactly what the sidebar shows.
  const onVisiblePhotosChangeRef = useRef(onVisiblePhotosChange)
  useEffect(() => {
    onVisiblePhotosChangeRef.current = onVisiblePhotosChange
  }, [onVisiblePhotosChange])

  const visibleKeysSignature = filteredPhotos.map(photo => photo.key).join('|')
  useEffect(() => {
    onVisiblePhotosChangeRef.current?.(filteredPhotos)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKeysSignature])

  // Get available years and blades from photos (recalculated when photos change)
  const availableYears = [...new Set(photos.map(photo =>
    photo.year || new Date(photo.upload_date || photo.createdAt || new Date().toISOString()).getFullYear()
  ))].sort((a, b) => b - a)

  // Auto-update selected year if current selection is not available
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0] ?? new Date().getFullYear()) // Set to most recent year
    }
  }, [availableYears, selectedYear])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isYearDropdownOpen && yearDropdownRef.current && !yearDropdownRef.current.contains(event.target as Node)) {
        setIsYearDropdownOpen(false)
      }
      if (isBladeDropdownOpen && bladeDropdownRef.current && !bladeDropdownRef.current.contains(event.target as Node)) {
        setIsBladeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isYearDropdownOpen, isBladeDropdownOpen])

  const _availableBlades = ['all', 'A', 'B', 'C']

  if (!isOpen) return null

  return (
    <div className="w-96 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className={`p-4 border-b ${theme.headerGradient}`}>
        <h3 className="text-lg font-semibold text-gray-900">
          {theme.title(t)}
        </h3>
        <p className="text-sm text-gray-600 mt-1">{t.selectPhoto}</p>

        {/* Modern Filters */}
        <div className="mt-4 space-y-4">
          {/* Year Filter - Material outlined */}
          <div className="relative z-40" ref={yearDropdownRef}>
            <label className={`absolute -top-2 left-3 px-1 bg-white rounded text-[10px] leading-none font-bold uppercase tracking-wider z-10 pointer-events-none ${theme.filterLabelText}`}>
              Yıl Filtresi
            </label>
              <button
                onClick={() => setIsYearDropdownOpen(prev => !prev)}
                className={`w-full text-left text-sm font-semibold bg-white border-2 rounded-lg px-3 py-2 flex items-center justify-between focus:outline-none transition-all duration-300 shadow-sm ${theme.selectBorder} ${theme.selectFocus} ${theme.selectHover}`}
              >
                <span>{selectedYear}</span>
                <svg className={`w-5 h-5 transition-transform ${theme.iconColor} ${isYearDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isYearDropdownOpen && (
                <div className={`absolute z-50 mt-2 w-full bg-white border rounded-lg shadow-xl overflow-hidden ${theme.filterCardBorder}`}>
                  {availableYears.map(year => (
                    <button
                      key={year}
                      onClick={() => {
                        setSelectedYear(year)
                        setIsYearDropdownOpen(false)
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        selectedYear === year
                          ? theme.selectActive
                          : `${theme.selectHoverBg} text-gray-700`
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
          </div>

          {/* Blade Filter - Material outlined */}
          <div className="relative z-30" ref={bladeDropdownRef}>
            <label className={`absolute -top-2 left-3 px-1 bg-white rounded text-[10px] leading-none font-bold uppercase tracking-wider z-10 pointer-events-none ${theme.filterLabelText}`}>
              Blade Seçimi
            </label>
              <button
                onClick={() => setIsBladeDropdownOpen(prev => !prev)}
                className={`w-full text-left text-sm font-semibold bg-white border-2 rounded-lg px-3 py-2 flex items-center justify-between focus:outline-none transition-all duration-300 shadow-sm ${theme.selectBorder} ${theme.selectFocus} ${theme.selectHover}`}
              >
                <span>{selectedBlade === 'all' ? "Tüm Blade'ler" : `Blade ${selectedBlade}`}</span>
                <svg className={`w-5 h-5 transition-transform ${theme.iconColor} ${isBladeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isBladeDropdownOpen && (
                <div className={`absolute z-20 mt-2 w-full bg-white border rounded-lg shadow-xl overflow-hidden ${theme.filterCardBorder}`}>
                  <button
                    onClick={() => {
                      setSelectedBlade('all')
                      setIsBladeDropdownOpen(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      selectedBlade === 'all'
                        ? theme.selectActive
                        : `${theme.selectHoverBg} text-gray-700`
                    }`}
                  >
                    Tüm Blade'ler
                  </button>
                  {['A', 'B', 'C'].map(blade => (
                    <button
                      key={blade}
                      onClick={() => {
                        setSelectedBlade(blade)
                        setIsBladeDropdownOpen(false)
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        selectedBlade === blade
                          ? theme.selectActive
                          : `${theme.selectHoverBg} text-gray-700`
                      }`}
                    >
                      Blade {blade}
                    </button>
                  ))}
                </div>
              )}
          </div>

        </div>
      </div>

      {/* Photos List */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Hata listenin USTUNDE, kendi baglaminda durur; eldeki fotograflar
            silinmez, sadece "guncel olmayabilir" bilgisi eklenir. */}
        <ErrorBanner
          message={loadError}
          onRetry={() => loadPhotos(true, true)}
          onDismiss={() => setLoadError(null)}
          className="mb-4"
        />

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className={`w-6 h-6 animate-spin ${theme.spinner}`} />
          </div>
        ) : filteredPhotos.length === 0 ? (
          // Yukleme hata verdiyse "fotograf bulunamadi" YAZILMAZ: bu, hatayi
          // "veri yok" gibi gosterip kullaniciyi yaniltirdi.
          loadError ? null : (
            <div className="text-center py-8">
              <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">{t.noPhotosFound}</p>
              <p className="text-gray-400 text-sm mt-2 max-w-md mx-auto">
                {selectedBlade === 'all'
                  ? `${selectedYear} yılına ait ${theme.countLabel} bulunamadı.`
                  : `${selectedYear} yılı Blade ${selectedBlade} için fotoğraf bulunamadı.`
                }
              </p>
            </div>
          )
        ) : (
          <div className="space-y-2">
            {filteredPhotos.map((photo) => (
              <div
                key={photo.key}
                className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedPhoto?.key === photo.key ? theme.listSelected : 'hover:bg-gray-100'
                }`}
                onClick={() => onPhotoSelect(photo)}
              >
                <div
                  className={`w-16 h-16 rounded-lg mr-3 flex items-center justify-center border overflow-hidden ${
                    selectedPhoto?.key === photo.key
                      ? theme.thumbSelected
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <img
                    src={photo.url ? `/api/proxy-image?url=${encodeURIComponent(photo.url)}&w=128` : ''}
                    alt={photo.originalName || 'Photo thumbnail'}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement | null
                      if (fallback) fallback.classList.remove('hidden')
                    }}
                  />
                  <Camera className="w-6 h-6 text-gray-400 hidden" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {photo.originalName || 'Unnamed Photo'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(() => {
                      const blade = getPhotoBlade(photo)
                      const bladeInfo = blade ? `Blade ${blade}` : ''
                      const yearInfo = photo.year ? `${photo.year}` : ''
                      const sizeInfo = photo.size ? `${(photo.size / 1024 / 1024).toFixed(1)} MB` : ''

                      return [bladeInfo, yearInfo, sizeInfo].filter(Boolean).join(' • ')
                    })()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default PhotoSidebar
